class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
        
        // Trigger free shipping bar update when cart drawer opens
        setTimeout(() => {
          const freeShippingBar = new FreeShippingBar();
          freeShippingBar.updateShippingBar();
        }, 200);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    
    // Dispatch custom event for cart opened
    document.dispatchEvent(new CustomEvent('cart:opened'));
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer', 'cart-drawer-recommendations'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
          
          // Free shipping bar is now handled by Liquid templating
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
          
          // Free shipping bar is now handled by Liquid templating
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Cart Drawer Recommendations functionality
class CartDrawerRecommendations extends HTMLElement {
  constructor() {
    super();
    this.container = this.querySelector('.cart-drawer__recommendations');
    this.swiperContainer = this.querySelector('.cart-drawer__recommendations-swiper');
    
    this.init();
  }

  init() {
    this.setupEventListeners();
    this.setupNavigationArrows();
  }

  setupEventListeners() {
    // Handle form submissions from recommendations
    this.container.addEventListener('submit', (event) => {
      const form = event.target.closest('form');
      if (form && form.closest('.cart-drawer__recommendation-item')) {
        event.preventDefault();
        this.handleProductAdd(form);
      }
    });

    // Listen for cart updates to remove added products
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source !== 'cart-recommendations') {
        this.removeAddedProducts(event.cartData);
      }
    });
  }

  setupNavigationArrows() {
    const prevButton = this.querySelector('.cart-drawer__swiper-button-prev');
    const nextButton = this.querySelector('.cart-drawer__swiper-button-next');
    const swiperContainer = this.querySelector('.cart-drawer__recommendations-swiper');
    
    if (!prevButton || !nextButton || !swiperContainer) return;
    
    // Set initial disabled state for first slide
    prevButton.classList.add('disabled');
    prevButton.disabled = true;
    
    // Wait for swiper to initialize
    setTimeout(() => {
      const swiper = swiperContainer.swiper;
      if (!swiper) return;
      
      // Update arrow states initially
      this.updateArrowStates(swiper);
      
      // Listen for slide changes
      swiper.on('slideChange', () => {
        this.updateArrowStates(swiper);
      });
    }, 100);
  }

  updateArrowStates(swiper) {
    const prevButton = this.querySelector('.cart-drawer__swiper-button-prev');
    const nextButton = this.querySelector('.cart-drawer__swiper-button-next');
    
    if (!prevButton || !nextButton) return;
    
    // Disable prev button if at first slide
    if (swiper.isBeginning) {
      prevButton.classList.add('disabled');
      prevButton.disabled = true;
    } else {
      prevButton.classList.remove('disabled');
      prevButton.disabled = false;
    }
    
    // Disable next button if at last slide
    if (swiper.isEnd) {
      nextButton.classList.add('disabled');
      nextButton.disabled = true;
    } else {
      nextButton.classList.remove('disabled');
      nextButton.disabled = false;
    }
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }



  handleProductAdd(form) {
    const formData = new FormData(form);
    const button = form.querySelector('button[type="submit"]');
    const originalText = button.querySelector('span').textContent;
    
    // Show loading state
    button.disabled = true;
    button.querySelector('span').textContent = 'Adding...';
    
    // Use the form's action URL or fallback to cart add endpoint
    const actionUrl = form.action || (window.routes && window.routes.cart_add_url) || '/cart/add.js';
    
    fetch(actionUrl, {
      method: 'POST',
      body: formData,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Content-Type': 'application/x-www-form-urlencoded',
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      if (data.status === 422) {
        throw new Error(data.description);
      }
      
      // Update cart
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-recommendations',
        cartData: data
      });
      
      // Update cart drawer
      const cartDrawerItems = document.querySelector('cart-drawer-items');
      if (cartDrawerItems) {
        cartDrawerItems.onCartUpdate();
      }
      
      // Show success state
      button.querySelector('span').textContent = 'Added!';
      
      // Remove product from recommendations after a delay
      setTimeout(() => {
        const productItem = form.closest('.cart-drawer__recommendation-item');
        if (productItem) {
          productItem.style.opacity = '0.5';
          productItem.style.pointerEvents = 'none';
          
          setTimeout(() => {
            productItem.remove();
            this.totalItems--;
            this.updateButtons();
          }, 1000);
        }
      }, 500);
      
    })
    .catch(error => {
      console.error('Error adding product to cart:', error);
      button.querySelector('span').textContent = 'Error';
      setTimeout(() => {
        button.disabled = false;
        button.querySelector('span').textContent = originalText;
      }, 2000);
    });
  }

  removeAddedProducts(cartData) {
    if (!cartData || !cartData.items) return;
    
    const cartProductIds = cartData.items.map(item => item.product_id);
    const recommendationItems = this.container.querySelectorAll('.cart-drawer__recommendation-item');
    
    recommendationItems.forEach(item => {
      const productId = parseInt(item.dataset.productId);
      if (cartProductIds.includes(productId)) {
        item.style.opacity = '0.5';
        item.style.pointerEvents = 'none';
        const button = item.querySelector('button');
        button.querySelector('span').textContent = 'Added!';
        button.disabled = true;
        
        setTimeout(() => {
          item.remove();
        }, 1000);
      }
    });
  }
}

customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);

// Free shipping bar is now handled by Liquid templating
